export const BUSINESS_TIME_ZONE = "America/Toronto"

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const MS_PER_DAY = 24 * 60 * 60 * 1000

function pad(value) {
    return String(value).padStart(2, "0")
}

function parseDateOnly(value) {
    if (typeof value !== "string" || !DATE_ONLY_PATTERN.test(value)) {
        return null
    }
    const [year, month, day] = value.split("-").map(Number)
    const utcDate = new Date(Date.UTC(year, month - 1, day))
    if (
        utcDate.getUTCFullYear() !== year ||
        utcDate.getUTCMonth() !== month - 1 ||
        utcDate.getUTCDate() !== day
    ) {
        return null
    }
    return { year, month, day }
}

function fromUtcDate(date) {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function formatPartsInBusinessTime(value) {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) {
        return null
    }

    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: BUSINESS_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date)

    const byType = parts.reduce((acc, part) => {
        acc[part.type] = part.value
        return acc
    }, {})

    return {
        year: Number(byType.year),
        month: Number(byType.month),
        day: Number(byType.day),
    }
}

function formatDateTimePartsInBusinessTime(value) {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return null
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: BUSINESS_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    }).formatToParts(date)
    const byType = parts.reduce((acc, part) => {
        acc[part.type] = part.value
        return acc
    }, {})
    return {
        year: Number(byType.year),
        month: Number(byType.month),
        day: Number(byType.day),
        hour: Number(byType.hour),
        minute: Number(byType.minute),
        second: Number(byType.second),
    }
}

function offsetAtInstant(value) {
    const date = value instanceof Date ? value : new Date(value)
    const parts = formatDateTimePartsInBusinessTime(date)
    if (!parts) return null
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) -
        Math.floor(date.getTime() / 1000) * 1000
}

export function formatBusinessDate(value = new Date()) {
    if (typeof value === "string" && DATE_ONLY_PATTERN.test(value)) {
        return value
    }

    const parts = formatPartsInBusinessTime(value)
    if (!parts) {
        return ""
    }

    return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

export function businessToday(now = new Date()) {
    return formatBusinessDate(now)
}

export function addBusinessDays(dateOnly, days) {
    const parsed = parseDateOnly(dateOnly)
    if (!parsed) {
        return ""
    }

    return fromUtcDate(new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day) + days * MS_PER_DAY))
}

export function addBusinessMonths(dateOnly, months) {
    const parsed = parseDateOnly(dateOnly)
    if (!parsed) {
        return ""
    }

    const targetMonthIndex = parsed.month - 1 + months
    const firstOfTarget = new Date(Date.UTC(parsed.year, targetMonthIndex, 1))
    const targetYear = firstOfTarget.getUTCFullYear()
    const targetMonth = firstOfTarget.getUTCMonth() + 1
    const targetDay = Math.min(parsed.day, daysInMonth(targetYear, targetMonth))
    return `${targetYear}-${pad(targetMonth)}-${pad(targetDay)}`
}

export function startOfBusinessMonth(dateOnly) {
    const parsed = parseDateOnly(dateOnly)
    return parsed ? `${parsed.year}-${pad(parsed.month)}-01` : ""
}

export function endOfBusinessMonth(dateOnly) {
    const parsed = parseDateOnly(dateOnly)
    return parsed ? `${parsed.year}-${pad(parsed.month)}-${pad(daysInMonth(parsed.year, parsed.month))}` : ""
}

export function startOfBusinessYear(dateOnly) {
    const parsed = parseDateOnly(dateOnly)
    return parsed ? `${parsed.year}-01-01` : ""
}

export function endOfBusinessYear(dateOnly) {
    const parsed = parseDateOnly(dateOnly)
    return parsed ? `${parsed.year}-12-31` : ""
}

export function getBusinessDateRangeForPreset(preset, now = new Date()) {
    const today = businessToday(now)

    switch (preset) {
        case "today":
            return { start: today, end: today }
        case "yesterday": {
            const yesterday = addBusinessDays(today, -1)
            return { start: yesterday, end: yesterday }
        }
        case "last7days":
            return { start: addBusinessDays(today, -6), end: today }
        case "last30days":
            return { start: addBusinessDays(today, -29), end: today }
        case "last90days":
            return { start: addBusinessDays(today, -89), end: today }
        case "last180days":
            return { start: addBusinessDays(today, -179), end: today }
        case "last3months":
            return { start: addBusinessMonths(today, -3), end: today }
        case "last6months":
            return { start: addBusinessMonths(today, -6), end: today }
        case "lastYear": {
            const lastYearDate = addBusinessMonths(today, -12)
            return { start: startOfBusinessYear(lastYearDate), end: endOfBusinessYear(lastYearDate) }
        }
        case "thisMonth":
            return { start: startOfBusinessMonth(today), end: today }
        case "lastMonth": {
            const lastMonthDate = addBusinessMonths(today, -1)
            return { start: startOfBusinessMonth(lastMonthDate), end: endOfBusinessMonth(lastMonthDate) }
        }
        case "thisYear":
            return { start: startOfBusinessYear(today), end: today }
        case "rollingYear":
            return { start: addBusinessDays(today, -364), end: today }
        default:
            return { start: addBusinessDays(today, -30), end: today }
    }
}

export function resolveDashboardDateRange(preset, customRange = {}, now = new Date()) {
    if (preset === "custom") {
        return {
            startDate: customRange.start || "",
            endDate: customRange.end || "",
        }
    }

    const range = getBusinessDateRangeForPreset(preset, now)
    return { startDate: range.start, endDate: range.end }
}

export function isBusinessDate(value) {
    return !!parseDateOnly(value)
}

export function businessDateToLocalCalendar(value) {
    const parsed = parseDateOnly(value)
    return parsed ? new Date(parsed.year, parsed.month - 1, parsed.day, 12) : null
}

export function localCalendarToBusinessDate(value) {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function formatBusinessInstant(value, options = {}) {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) {
        return ""
    }
    return new Intl.DateTimeFormat(options.locale || "en-CA", {
        timeZone: BUSINESS_TIME_ZONE,
        year: options.timeOnly ? undefined : "numeric",
        month: options.timeOnly ? undefined : options.month || "short",
        day: options.timeOnly ? undefined : "2-digit",
        hour: options.dateOnly ? undefined : "numeric",
        minute: options.dateOnly ? undefined : "2-digit",
        second: options.includeSeconds ? "2-digit" : undefined,
        hour12: options.dateOnly ? undefined : true,
        timeZoneName: options.timeZoneName ? "short" : undefined,
    }).format(date)
}

export function businessDateTimeLocalFromInstant(value) {
    if (value === null || value === undefined || value === "") return ""
    const parts = formatDateTimePartsInBusinessTime(value)
    if (!parts) return ""
    return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`
}

export function businessDateTimeLocalToUtc(value, existingInstant = null) {
    const match = typeof value === "string"
        ? value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/)
        : null
    if (!match) return null
    if (existingInstant && businessDateTimeLocalFromInstant(existingInstant) === value) {
        const existing = existingInstant instanceof Date ? existingInstant : new Date(existingInstant)
        if (!Number.isNaN(existing.getTime())) return existing.toISOString()
    }
    const [, year, month, day, hour, minute, second = "00"] = match
    const localEpoch = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second))
    const offsets = new Set([
        offsetAtInstant(new Date(localEpoch - MS_PER_DAY)),
        offsetAtInstant(new Date(localEpoch)),
        offsetAtInstant(new Date(localEpoch + MS_PER_DAY)),
    ])
    const expected = `${year}-${month}-${day}T${hour}:${minute}`
    const candidates = Array.from(offsets)
        .filter(offset => offset !== null)
        .map(offset => new Date(localEpoch - offset))
        .filter((candidate, index, all) => all.findIndex(item => item.getTime() === candidate.getTime()) === index)
        .filter(candidate => businessDateTimeLocalFromInstant(candidate) === expected)
    return candidates.length === 1 ? candidates[0].toISOString() : null
}

export function formatBusinessDateOnly(dateOnly, options = {}) {
    const parsed = parseDateOnly(dateOnly)
    if (!parsed) {
        return ""
    }
    return new Intl.DateTimeFormat(options.locale || "en-CA", {
        timeZone: "UTC",
        year: options.year || "numeric",
        month: options.month || "short",
        day: options.day || "2-digit",
    }).format(new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day, 12)))
}

export function getPreviousBusinessPeriod(startDate, endDate) {
    const start = parseDateOnly(startDate)
    const end = parseDateOnly(endDate)
    if (!start || !end) {
        return null
    }

    const startTime = Date.UTC(start.year, start.month - 1, start.day)
    const endTime = Date.UTC(end.year, end.month - 1, end.day)
    if (endTime < startTime) {
        return null
    }

    const periodLength = Math.floor((endTime - startTime) / MS_PER_DAY) + 1
    const prevEndDate = addBusinessDays(startDate, -1)
    const prevStartDate = addBusinessDays(prevEndDate, -periodLength + 1)
    return { start: prevStartDate, end: prevEndDate }
}

function formatMonthDay(dateOnly) {
    const parsed = parseDateOnly(dateOnly)
    if (!parsed) {
        return ""
    }

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day, 12)))
}

export function formatBusinessChartLabel(item, groupBy = "day") {
    if (item && item.label) {
        return item.label
    }

    const dateOnly = typeof item === "string" ? item : item?.businessDate || formatBusinessDate(item?.timestamp || item?.bucketStartIso)
    const parsed = parseDateOnly(dateOnly)
    if (!parsed) {
        return ""
    }

    if (groupBy === "month") {
        return new Intl.DateTimeFormat("en-US", {
            month: "short",
            year: "numeric",
            timeZone: "UTC",
        }).format(new Date(Date.UTC(parsed.year, parsed.month - 1, 1, 12)))
    }

    if (groupBy === "week") {
        return `Week of ${formatMonthDay(dateOnly)}`
    }

    return formatMonthDay(dateOnly)
}

export function formatBusinessDateRangeLabel(startDate, endDate) {
    const startLabel = formatMonthDay(startDate)
    const endLabel = formatMonthDay(endDate)
    const start = parseDateOnly(startDate)
    const end = parseDateOnly(endDate)
    if (!startLabel || !endLabel || !start || !end) {
        return ""
    }

    return `${startLabel}, ${start.year} - ${endLabel}, ${end.year}`
}
